import { TZDate } from '@date-fns/tz'
import {
  addDays,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns'
import type { TFunction } from 'i18next'
import { formatDate } from '@/utils/format'

/** Periods offered wherever stats are shown. */
export type PresetDateRange =
  | 'today'
  | 'yesterday'
  | '6h'
  | '12h'
  | '24h'
  | '7d'
  | '30d'
  | '90d'
  | '6m'
  | '12m'
  | 'lastWeek'
  | 'lastMonth'
  | 'lastYear'
  | 'thisWeek'
  | 'thisMonth'
  | 'thisYear'

/**
 * A period the user picked themselves, as `custom:<from>:<to>` with both ends
 * calendar dates (`yyyy-MM-dd`) in the network's time zone, both inclusive.
 *
 * It is a string rather than an object so a range stays one opaque token: it
 * goes into a query key, into localStorage and through every `range` prop the
 * same way a preset does.
 */
export type CustomDateRange = `custom:${string}`

/**
 * The object's own flight. Unlike every other range this one is relative to
 * whatever is being shown, so it stays meaningful as you move between line
 * items — picking it once follows you, where a custom range of one line item's
 * dates would not.
 */
export const FLIGHT_RANGE = 'flight'

export type DateRange = PresetDateRange | CustomDateRange | typeof FLIGHT_RANGE

/** The days reached for most, offered as one click. */
export const QUICK_DATE_RANGES: PresetDateRange[] = ['today', 'yesterday']

/**
 * The rest, folded under "Last" and "This" so the list stays short. Each group
 * holds sections drawn with a rule between them: "Last" puts the rolling
 * periods before the calendar ones, since "last 7 days" and "last week" answer
 * different questions.
 */
export const DATE_RANGE_GROUPS: {
  key: 'last' | 'this'
  labelKey: string
  sections: PresetDateRange[][]
}[] = [
  {
    key: 'last',
    labelKey: 'stats.rangeGroupLast',
    sections: [
      ['6h', '12h', '24h', '7d', '30d', '90d', '6m', '12m'],
      ['lastWeek', 'lastMonth', 'lastYear'],
    ],
  },
  {
    key: 'this',
    labelKey: 'stats.rangeGroupThis',
    sections: [['thisWeek', 'thisMonth', 'thisYear']],
  },
]

/** Every preset, whichever way the picker lays them out. */
export const PRESET_DATE_RANGES: PresetDateRange[] = [
  ...QUICK_DATE_RANGES,
  ...DATE_RANGE_GROUPS.flatMap((group) => group.sections.flat()),
]

/**
 * Weeks start on Monday. The network profile can name another start day
 * (`adnDefaults.startDayCalendar`), which these periods do not read yet.
 */
const WEEK_STARTS_ON = 1

const CUSTOM_PATTERN = /^custom:(\d{4}-\d{2}-\d{2}):(\d{4}-\d{2}-\d{2})$/

/** Calendar dates of a custom range, or null when the range is a preset. */
export function parseCustomRange(range: DateRange): { from: string; to: string } | null {
  const match = CUSTOM_PATTERN.exec(range)
  return match ? { from: match[1], to: match[2] } : null
}

export function isCustomRange(range: DateRange): range is CustomDateRange {
  return parseCustomRange(range) !== null
}

/** Builds the range token for two calendar dates, earliest end first. */
export function customRange(from: Date, to: Date): CustomDateRange {
  const [start, end] = from <= to ? [from, to] : [to, from]
  return `custom:${toCalendarDate(start)}:${toCalendarDate(end)}`
}

/** A date as `yyyy-MM-dd`, read in whatever zone the date itself carries. */
export function toCalendarDate(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** Splits a `yyyy-MM-dd` into a date at midnight in `timeZone`. */
function calendarDateIn(date: string, timeZone: string): TZDate {
  const [year, month, day] = date.split('-').map(Number)
  return new TZDate(year, month - 1, day, timeZone)
}

/**
 * The custom range covering an object's own flight — its start and end dates as
 * calendar days in the network's zone, which is how a custom range is spelled.
 * Null when the object has no dates to cover.
 */
export function flightRange(
  startDate: string | undefined,
  endDate: string | undefined,
  timeZone: string
): CustomDateRange | null {
  const from = calendarDateOf(startDate, timeZone)
  const to = calendarDateOf(endDate, timeZone)
  if (!from || !to) return null
  return from <= to ? `custom:${from}:${to}` : `custom:${to}:${from}`
}

/** An instant as the calendar date it falls on in `timeZone`. */
function calendarDateOf(iso: string | undefined, timeZone: string): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return toCalendarDate(new TZDate(date, timeZone))
}

/** The dates an object runs between, which `FLIGHT_RANGE` resolves against. */
export interface Flight {
  startDate?: string
  endDate?: string
}

/**
 * Turns the object-relative flight range into the concrete period it means for
 * this object. Everything else is already concrete and passes straight through.
 *
 * An object with no dates falls back to 30 days, so a stats view still shows
 * something rather than nothing.
 */
export function resolveRange(range: DateRange, flight: Flight | undefined, timeZone: string): DateRange {
  if (range !== FLIGHT_RANGE) return range
  return flightRange(flight?.startDate, flight?.endDate, timeZone) ?? '30d'
}

/** Translation key for each preset, so every picker names them the same way. */
export const DATE_RANGE_LABEL_KEYS: Record<PresetDateRange | typeof FLIGHT_RANGE, string> = {
  flight: 'lineItems.campaignPeriod',
  today: 'stats.today',
  yesterday: 'stats.yesterday',
  '6h': 'stats.last6h',
  '12h': 'stats.last12h',
  '24h': 'stats.past24h',
  '7d': 'stats.last7d',
  '30d': 'stats.last30d',
  '90d': 'stats.last90d',
  '6m': 'stats.last6m',
  '12m': 'stats.last12m',
  lastWeek: 'stats.lastWeek',
  lastMonth: 'stats.lastMonth',
  lastYear: 'stats.lastYear',
  thisWeek: 'stats.thisWeek',
  thisMonth: 'stats.thisMonth',
  thisYear: 'stats.thisYear',
}

/** Ranges short enough to plot per half-hour rather than per day. */
export function isIntraday(range: DateRange): boolean {
  const custom = parseCustomRange(range)
  // A custom range of one day is as short as "today", so it plots the same way.
  if (custom) return custom.from === custom.to
  return (
    range === 'today' ||
    range === 'yesterday' ||
    range === '6h' ||
    range === '12h' ||
    range === '24h'
  )
}

/** Length of each rolling hour period. */
const HOUR_RANGES: Partial<Record<DateRange, number>> = { '6h': 6, '12h': 12, '24h': 24 }

/**
 * What a range is called. Presets have a translation; a custom range names its
 * own two dates, in the browser's locale.
 */
export function dateRangeLabel(range: DateRange, t: TFunction): string {
  const custom = parseCustomRange(range)
  if (!custom) return t(DATE_RANGE_LABEL_KEYS[range as PresetDateRange])
  const format = (value: string) =>
    formatDate(`${value}T00:00:00`, { day: 'numeric', month: 'short' })
  return custom.from === custom.to
    ? format(custom.from)
    : `${format(custom.from)} – ${format(custom.to)}`
}

function ceilToHalfHour(date: TZDate): TZDate {
  const ceiled = new TZDate(date.getTime(), date.timeZone)
  const mins = date.getMinutes()
  if (mins === 0 || mins === 30) {
    ceiled.setSeconds(0, 0)
  } else {
    ceiled.setMinutes(mins <= 30 ? 30 : 60, 0, 0)
  }
  return ceiled
}

function toUtcIso(date: Date): string {
  return new Date(date.getTime()).toISOString()
}

export function getDateRange(
  range: DateRange,
  timeZone: string
): { startDate: string; endDate: string } {
  const custom = parseCustomRange(range)
  if (custom) {
    const now = new TZDate(new Date(), timeZone)
    const start = startOfDay(calendarDateIn(custom.from, timeZone))
    // The end date is inclusive, so the window runs to the start of the day
    // after it — but never past now, which would only ask for empty buckets.
    const end = startOfDay(addDays(calendarDateIn(custom.to, timeZone), 1))
    const cappedEnd = end.getTime() > now.getTime() ? ceilToHalfHour(now) : end
    return { startDate: toUtcIso(start), endDate: toUtcIso(cappedEnd) }
  }
  const hours = HOUR_RANGES[range]
  if (hours) {
    const end = new Date()
    const start = new Date(end.getTime() - hours * 60 * 60_000)
    return { startDate: start.toISOString(), endDate: end.toISOString() }
  }
  const now = new TZDate(new Date(), timeZone)
  // Today and yesterday are calendar days in the network's time zone.
  if (range === 'today') {
    return { startDate: toUtcIso(startOfDay(now)), endDate: toUtcIso(ceilToHalfHour(now)) }
  }
  if (range === 'yesterday') {
    return {
      startDate: toUtcIso(startOfDay(subDays(now, 1))),
      endDate: toUtcIso(startOfDay(now)),
    }
  }
  // Calendar periods start on the first of the week, month or year in the
  // network's zone.
  if (range === 'thisWeek') {
    return {
      startDate: toUtcIso(startOfWeek(now, { weekStartsOn: WEEK_STARTS_ON })),
      endDate: toUtcIso(ceilToHalfHour(now)),
    }
  }
  if (range === 'lastWeek') {
    return {
      startDate: toUtcIso(startOfWeek(subWeeks(now, 1), { weekStartsOn: WEEK_STARTS_ON })),
      endDate: toUtcIso(startOfWeek(now, { weekStartsOn: WEEK_STARTS_ON })),
    }
  }
  if (range === 'thisMonth') {
    return { startDate: toUtcIso(startOfMonth(now)), endDate: toUtcIso(ceilToHalfHour(now)) }
  }
  if (range === 'lastMonth') {
    return {
      startDate: toUtcIso(startOfMonth(subMonths(now, 1))),
      endDate: toUtcIso(startOfMonth(now)),
    }
  }
  if (range === 'thisYear') {
    return { startDate: toUtcIso(startOfYear(now)), endDate: toUtcIso(ceilToHalfHour(now)) }
  }
  if (range === 'lastYear') {
    return {
      startDate: toUtcIso(startOfYear(subYears(now, 1))),
      endDate: toUtcIso(startOfYear(now)),
    }
  }
  const end = ceilToHalfHour(now)
  if (range === '6m' || range === '12m') {
    const start = startOfDay(subMonths(now, range === '6m' ? 6 : 12))
    return { startDate: toUtcIso(start), endDate: toUtcIso(end) }
  }
  // `flight` should have been resolved before it got here; 30 days is the
  // harmless fallback if some caller forgot to.
  const days = range === '7d' ? 7 : range === '90d' ? 90 : 30
  const start = startOfDay(subDays(now, days))
  return { startDate: toUtcIso(start), endDate: toUtcIso(end) }
}

/** Reads a remembered range, falling back when nothing valid is stored. */
export function getSavedRange(key: string, fallback: DateRange): DateRange {
  const saved = localStorage.getItem(key)
  if (!saved) return fallback
  if (saved === FLIGHT_RANGE) return FLIGHT_RANGE
  if (PRESET_DATE_RANGES.includes(saved as PresetDateRange)) return saved as PresetDateRange
  if (CUSTOM_PATTERN.test(saved)) return saved as CustomDateRange
  return fallback
}

/**
 * The flight covering a set of objects — earliest start to latest end. Lets a
 * parent offer its children's period as one range, the way an order stands for
 * the line items under it.
 *
 * Undefined when nothing in the set has both dates, which is what `flight`
 * means "no flight to offer" with.
 */
export function spanFlight(flights: Flight[]): Flight | undefined {
  const dated = flights.filter((f) => f.startDate && f.endDate)
  if (dated.length === 0) return undefined
  // Compared as instants rather than strings: the dates come back from the api
  // with whatever offset each was written in, so text order is not time order.
  const at = (iso: string) => new Date(iso).getTime()
  const earliest = dated.reduce((a, b) => (at(b.startDate!) < at(a.startDate!) ? b : a))
  const latest = dated.reduce((a, b) => (at(b.endDate!) > at(a.endDate!) ? b : a))
  return { startDate: earliest.startDate, endDate: latest.endDate }
}
